import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  AgentInstallDecide,
  AgentInstallRequest,
  AgentPublishRequest,
} from "@/api/types";

const KEY_AGENT_INSTALL_REQUESTS = (agentId: string) =>
  ["agent-install-requests", agentId] as const;
const KEY_MY_PENDING_PUBLISH = ["me", "pending-publish-requests"] as const;
const KEY_MY_PENDING_INSTALL = ["me", "pending-install-requests"] as const;

/** Lista install requests de UM agente especifico (visivel para
 * manager/admin do owner_team). */
export function useAgentInstallRequests(agentId: string) {
  return useQuery({
    queryKey: KEY_AGENT_INSTALL_REQUESTS(agentId),
    queryFn: () =>
      api.GET<AgentInstallRequest[]>(`/agents/${agentId}/install-requests`),
    enabled: Boolean(agentId),
  });
}

export function useDecideInstall(agentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      requestId: string;
      body: AgentInstallDecide;
    }) =>
      api.POST<AgentInstallRequest>(
        `/agents/${agentId}/install-requests/${params.requestId}/decide`,
        params.body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_AGENT_INSTALL_REQUESTS(agentId) });
      void qc.invalidateQueries({ queryKey: KEY_MY_PENDING_INSTALL });
    },
  });
}

/**
 * Lista publish requests pendentes que ESTE user pode decidir
 * (manager do owner_team OR admin OR approval_responsible).
 *
 * Endpoint: GET /me/pending-publish-requests
 */
export function usePendingPublishRequests() {
  return useQuery({
    queryKey: KEY_MY_PENDING_PUBLISH,
    queryFn: () =>
      api.GET<AgentPublishRequest[]>("/me/pending-publish-requests"),
    refetchInterval: 60_000,
  });
}

/**
 * Lista install requests pendentes que ESTE user pode decidir.
 *
 * Endpoint: GET /me/pending-install-requests
 */
export function usePendingInstallRequests() {
  return useQuery({
    queryKey: KEY_MY_PENDING_INSTALL,
    queryFn: () =>
      api.GET<AgentInstallRequest[]>("/me/pending-install-requests"),
    refetchInterval: 60_000,
  });
}
